/**
 * Audiencia del usuario: Fiado SA (BACKOFFICE) vs UNE-Credito (PARTNER).
 *
 * DEC/TD-CRED-001: la audiencia se deriva del ROL RBAC, no del legacy
 * `customFields.typeOfDirectoryId` (que la migración RBAC dejó de poblar → todos
 * caían a PARTNER y Fiado SA perdía su sidebar + el switcher).
 *
 * Principio: ves un lado SOLO si tenés el rol de ese lado.
 *   - `une_fiadosa` → derecho a la vista BACKOFFICE (Fiado SA).
 *   - `une_partner` → derecho a la vista PARTNER (UNE-Credito).
 * El switcher aparece solo si tenés AMBOS roles; con uno solo quedás fijo en su vista.
 */
import type { RoleAssignment } from '@/types/rbac-auth.types';

export type Audience = 'BACKOFFICE' | 'PARTNER';

/** roleId del rol de staff Fiado SA (operador interno) → vista BACKOFFICE. */
export const FIADOSA_ROLE_ID = 'une_fiadosa';
/** roleId del rol partner UNE → vista PARTNER. */
export const PARTNER_ROLE_ID = 'une_partner';

const hasRole = (roleAssignments: RoleAssignment[] | undefined, roleId: string): boolean =>
  (roleAssignments ?? []).some((r) => r.roleId === roleId);

/** Fiado SA = tiene el rol `une_fiadosa` (derecho a la vista BACKOFFICE). */
export function isFiadoSaUser(roleAssignments: RoleAssignment[] | undefined): boolean {
  return hasRole(roleAssignments, FIADOSA_ROLE_ID);
}

/** Partner UNE = tiene el rol `une_partner` (derecho a la vista PARTNER). */
export function isPartnerUser(roleAssignments: RoleAssignment[] | undefined): boolean {
  return hasRole(roleAssignments, PARTNER_ROLE_ID);
}

/** El switcher se muestra solo si el usuario tiene AMBOS roles (puede operar en las dos vistas). */
export function canSwitchAudience(roleAssignments: RoleAssignment[] | undefined): boolean {
  return isFiadoSaUser(roleAssignments) && isPartnerUser(roleAssignments);
}

/**
 * Audiencia efectiva. El override del switcher (`activeRole`) manda SOLO si el usuario
 * tiene derecho a esa vista (un override viejo/persistido no te mete en un lado sin el rol).
 * Si no hay override válido: BACKOFFICE si es Fiado SA, si no PARTNER.
 */
export function resolveAudience(
  activeRole: string | null,
  roleAssignments: RoleAssignment[] | undefined,
): Audience {
  const canBackoffice = isFiadoSaUser(roleAssignments);
  const canPartner = isPartnerUser(roleAssignments);
  if (activeRole === 'BACKOFFICE' && canBackoffice) return 'BACKOFFICE';
  if (activeRole === 'PARTNER' && canPartner) return 'PARTNER';
  return canBackoffice ? 'BACKOFFICE' : 'PARTNER';
}
